import { z } from 'zod';

import { accountTypeForMandate } from '../accountMandate';
import { ACCOUNT_TYPES, BROKERS, PORTFOLIO_MANDATES } from '../enums';

export const AccountIdSchema = z.string().trim().min(1, 'Account is required.');

const ClassificationFields = {
  mandate: z.enum(PORTFOLIO_MANDATES).optional(),
  accountType: z.enum(ACCOUNT_TYPES).optional(),
};

function addClassificationIssues(
  value: {
    mandate?: (typeof PORTFOLIO_MANDATES)[number] | undefined;
    accountType?: (typeof ACCOUNT_TYPES)[number] | undefined;
  },
  ctx: z.RefinementCtx,
  mandateRequired: boolean,
): void {
  if (mandateRequired && value.mandate === undefined && value.accountType === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Account mandate is required.',
      path: ['mandate'],
    });
  }

  if (
    value.mandate !== undefined &&
    value.accountType !== undefined &&
    accountTypeForMandate(value.mandate) !== value.accountType
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Account type must match the selected mandate.',
      path: ['accountType'],
    });
  }
}

export const CreateAccountSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    broker: z.enum(BROKERS),
    ...ClassificationFields,
    currency: z
      .string()
      .trim()
      .length(3)
      .transform((value) => value.toUpperCase()),
    externalId: z.string().trim().min(1).max(64).optional(),
    externalAccountIds: z.array(z.string().trim().min(1).max(64)).optional(),
  })
  .superRefine((value, ctx) => addClassificationIssues(value, ctx, true));

export const UpdateAccountSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    ...ClassificationFields,
    confirmMandateChange: z.boolean().optional(),
    externalId: z.string().trim().min(1).max(64).nullable().optional(),
    externalAccountIds: z.array(z.string().trim().min(1).max(64)).optional(),
  })
  .superRefine((value, ctx) => {
    addClassificationIssues(value, ctx, false);
    if (
      value.name === undefined &&
      value.accountType === undefined &&
      value.mandate === undefined &&
      value.externalId === undefined &&
      value.externalAccountIds === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one account field must be provided.',
      });
    }
  });

export type CreateAccountInput = z.infer<typeof CreateAccountSchema>;
export type UpdateAccountInput = z.infer<typeof UpdateAccountSchema>;
